import api from "../../utils/api";

/**
 * Play a level — sends prompt to server, gets AI response + win/score determination.
 * Replaces the old client-side flow: generateGPTResponse → checkAnswer → getLevelScore.
 *
 * @param {number} level - 0-based level index
 * @param {string} prompt - user's current message
 * @param {Array} history - previous messages [{role: "user"|"assistant", content: string}]
 * @param {string} model - optional model override
 * @param {Array} repeatMessages - previously-won prompts for anti-repeat
 * @param {number} totalSymbols - total characters used so far in this attempt
 * @returns {Promise<{response: string, isWon: boolean, score: number|null, messageCount: number, totalSymbols: number}>}
 */
export const playLevel = async (
  level,
  prompt,
  history = [],
  model = null,
  repeatMessages = [],
  totalSymbols = 0
) => {
  const body = {
    level,
    prompt,
    history,
    repeatMessages,
    totalSymbols,
  };
  if (model) body.model = model;

  return api
    .post("/game/play", body)
    .then((response) => response.data)
    .catch((error) => {
      if (
        error?.response?.data?.message
      ) {
        throw Error(error.response.data.message);
      } else {
        throw Error("Something went wrong with the AI. Please try again.");
      }
    });
};

/**
 * Get public level metadata (names, introductions — no secrets).
 * @returns {Promise<{levels: Array, total: number}>}
 */
export const getLevels = async () => {
  return api
    .get("/game/levels")
    .then((response) => response.data)
    .catch((error) => {
      console.error("Failed to fetch levels:", error);
      throw error;
    });
};

/**
 * Use a clue for a level (auth required, decrements clue count server-side).
 * @param {number} level - 0-based level index
 * @returns {Promise<{clue: string, remainingClues: number}>}
 */
export const useClue = async (level) => {
  return api
    .post("/game/clue", { level })
    .then((response) => response.data)
    .catch((error) => {
      if (error?.response?.status === 403) {
        throw Error("No clues remaining");
      }
      throw Error("Failed to get clue");
    });
};
